<template>
  <Layout>
    <div :class="['wa-messenger', { 'detail-open': showDetail }]">
      <!-- ═══ SIDEBAR ═══ -->
      <div class="wa-sidebar">
        <div class="wa-sidebar-header">
          <div class="wa-sidebar-header-row">
            <h2>Conversas <span v-if="conversations.length" class="count">{{ conversations.length }}</span></h2>
            <div class="wa-sidebar-actions">
              <button class="wa-sidebar-icon-btn" :title="showingArchived ? 'Voltar' : 'Arquivados'" @click="toggleArchived">
                <i :class="showingArchived ? 'bx bx-arrow-back' : 'bx bx-archive-in'"></i>
              </button>
              <button class="wa-sidebar-icon-btn" title="Nova conversa" @click="showNewConv = true">
                <i class="bx bx-edit-alt"></i>
              </button>
            </div>
          </div>
        </div>

        <div class="wa-search">
          <i class="bx bx-search s-icon"></i>
          <input type="text" v-model="searchQuery" placeholder="Buscar conversa..." @input="onSearch" />
        </div>

        <div v-if="!showingArchived" class="wa-filters">
          <button v-for="f in filters" :key="f.value" :class="['wa-filter-tab', { active: activeFilter === f.value }]" @click="setFilter(f.value)">
            {{ f.label }}
          </button>
        </div>
        <div v-else class="wa-filters">
          <button class="wa-filter-tab active">Arquivados</button>
        </div>

        <div class="wa-conv-list">
          <div v-if="loadingConvs" class="text-center p-4"><b-spinner small variant="primary"></b-spinner></div>
          <div v-else-if="conversations.length === 0" class="text-center p-4" style="color: #94A3B8; font-size: 13px;">Nenhuma conversa</div>
          <div v-for="c in conversations" :key="c.id" :class="['wa-conv-item', { active: selConvId === c.id }]" @click="openConv(c)">
            <div class="wa-avatar">
              {{ initials(c) }}
              <span v-if="hasSession(c)" class="online-dot"></span>
            </div>
            <div class="wa-conv-body">
              <div class="wa-conv-top">
                <span class="wa-conv-name">{{ dispName(c) }}</span>
                <span class="wa-conv-time">{{ fmtTime(c.last_message_at) }}</span>
              </div>
              <div class="wa-conv-preview">{{ convPreview(c) }}</div>
              <div class="wa-conv-meta">
                <span :class="['wa-badge', statusCls(c)]">{{ statusLbl(c) }}</span>
                <span v-if="c.assigned_admin" class="wa-badge agent">{{ c.assigned_admin.name }}</span>
                <span v-if="c.unread_count > 0" class="wa-unread">{{ c.unread_count }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ═══ CHAT ═══ -->
      <div class="wa-chat">
        <div v-if="!selConvId || !conv" class="wa-empty">
          <div>
            <i class="bx bx-chat e-icon"></i>
            <h5>Selecione uma conversa</h5>
            <p>Ou inicie uma nova conversa</p>
          </div>
        </div>
        <template v-else>
          <!-- Header -->
          <div class="wa-chat-header">
            <div class="wa-avatar sm">{{ initials(conv) }}</div>
            <div class="wa-chat-info">
              <div class="name" @click="showDetail = !showDetail">
                {{ dispName(conv) }}
                <i class="bx bxl-whatsapp" style="color: #25D366; font-size: 16px;"></i>
              </div>
              <div class="sub">
                {{ fmtPhone(conv.phone) }}
                <span v-if="conv.session_expires_at" :class="['wa-timer', { expired: !sActive }]">
                  <i class="bx bx-time-five" style="font-size: 12px;"></i>
                  {{ sActive ? 'Janela: ' + countdown : 'Expirada' }}
                </span>
              </div>
            </div>
            <div class="wa-chat-btns">
              <span v-if="conv.contact" :class="['wa-header-badge', pStatusCls(conv.contact.status)]">{{ pStatusLbl(conv.contact.status, conv.contact.ref_status_name) }}</span>

              <!-- Conversa ativa/bot/pendente -->
              <template v-if="!conv.archived_at && conv.status !== 'closed'">
                <button class="wa-btn-outlined" @click="showTransfer = true">
                  <i class="bx bx-transfer"></i> Transferir
                </button>
                <div class="wa-divider-v"></div>
                <b-dropdown split right variant="primary" @click="doComplete" class="wa-split-dropdown">
                  <template #button-content>
                    <i class="bx bx-check mr-1"></i> Concluir
                  </template>
                  <b-dropdown-item v-if="conv.contact" @click="goParticipant"><i class="bx bx-user mr-1"></i> Ver Participante</b-dropdown-item>
                </b-dropdown>
              </template>

              <!-- Conversa arquivada/concluída -->
              <template v-else>
                <button class="wa-btn-outlined" @click="doReopen">
                  <i class="bx bx-revision"></i> Reabrir
                </button>
              </template>
            </div>
          </div>

          <!-- Messages -->
          <div ref="msgBox" class="wa-messages" @scroll="onMessagesScroll">
            <div v-if="loadingMore" class="text-center py-2"><b-spinner small variant="secondary"></b-spinner></div>
            <div v-if="loadingMsgs" class="text-center p-4"><b-spinner small variant="primary"></b-spinner></div>
            <template v-else>
              <template v-for="(grp, dk) in groupedMsgs">
                <div :key="'d'+dk" class="wa-date-divider"><span>{{ dk }}</span></div>
                <template v-for="(m, mIdx) in grp">
                  <!-- Separador: sessão expirada (entre última msg inbound e próxima outbound com gap > 24h) -->
                  <div v-if="showSessionExpiredSep(grp, mIdx)" :key="'exp'+m.id" class="wa-system-separator warn">
                    <i class="bx bx-time-five"></i>
                    Tempo de sessão excedido em 24h às <strong>{{ fmtSessionExpiry(grp, mIdx) }}</strong>
                  </div>
                  <!-- Divisor de sistema -->
                  <div v-if="m.message_type === 'system'" :key="'sys'+m.id" :class="['wa-system-divider', sysDividerType(m)]">
                    <div class="wa-divider-line"></div>
                    <div class="wa-divider-content">
                      <i :class="sysDividerIcon(m)"></i>
                      <span v-html="sysDividerText(m)"></span>
                    </div>
                    <div class="wa-divider-line"></div>
                  </div>
                  <!-- Mensagem normal -->
                  <div v-else :key="m.id" :class="['wa-msg', m.direction==='outbound' ? 'out' : 'in']">
                    <div class="wa-msg-wrap">
                    <div v-if="m.message_type === 'template'" class="wa-tpl-bubble">
                      <div class="tpl-header"><i class="bx bx-file mr-1"></i> Template</div>
                      <img v-if="mediaUrl(m)" :src="mediaUrl(m)" style="width:100%;border-radius:4px 4px 0 0;display:block;margin-bottom:6px;" />
                      <div class="tpl-body" v-html="fmtMsg(m.content || m.body)"></div>
                      <div class="wa-msg-meta" style="padding: 4px 12px 8px;">{{ fmtMsgTime(m.sent_at) }} <span v-if="m.direction==='outbound'" class="check"><i :class="checkIcon(m)"></i></span></div>
                    </div>
                    <div v-else-if="m.message_type === 'image'" class="wa-bubble">
                      <a v-if="mediaUrl(m)" :href="mediaUrl(m)" target="_blank"><img :src="mediaUrl(m)" style="max-width: 240px; border-radius: 8px; display: block;" /></a>
                      <div v-if="m.content" v-html="fmtMsg(m.content)" style="margin-top: 4px;"></div>
                      <div class="wa-msg-meta">{{ fmtMsgTime(m.sent_at) }} <span v-if="m.direction==='outbound'" class="check"><i :class="checkIcon(m)"></i></span></div>
                    </div>
                    <a v-else-if="m.message_type === 'document'" :href="mediaUrl(m)" target="_blank" class="wa-doc-bubble">
                      <div class="doc-ext">{{ fileExt(m.media_filename) }}</div>
                      <div><div class="doc-name">{{ m.media_filename || 'Documento' }}</div><div class="doc-size">{{ fmtMsgTime(m.sent_at) }}</div></div>
                    </a>
                    <div v-else-if="m.message_type === 'audio'" class="wa-bubble">
                      <audio v-if="mediaUrl(m)" :src="mediaUrl(m)" controls style="max-width: 240px;"></audio>
                      <div class="wa-msg-meta">{{ fmtMsgTime(m.sent_at) }}</div>
                    </div>
                    <div v-else-if="m.message_type === 'video'" class="wa-bubble">
                      <video v-if="mediaUrl(m)" :src="mediaUrl(m)" controls style="max-width: 240px; border-radius: 8px;"></video>
                      <div class="wa-msg-meta">{{ fmtMsgTime(m.sent_at) }}</div>
                    </div>
                    <div v-else class="wa-bubble">
                      <div v-if="m.direction==='inbound'" class="wa-msg-sender">{{ m.sender_name || 'Participante' }}</div>
                      <div v-if="m.direction==='outbound' && m.sender === 'bot'" class="wa-sender-badge bot"><i class="bx bx-bot"></i> IA BOT</div>
                      <div v-else-if="m.direction==='outbound' && m.sender_name" class="wa-sender-badge agent"><i class="bx bx-user"></i> {{ m.sender_name }}</div>
                      <div v-html="fmtMsg(m.content || m.body || '')"></div>
                      <div class="wa-msg-meta">{{ fmtMsgTime(m.sent_at) }} <span v-if="m.direction==='outbound'" class="check"><i :class="checkIcon(m)"></i></span></div>
                    </div>
                    <!-- Dropdown detalhes da mensagem -->
                    <b-dropdown no-caret right variant="link" size="sm" class="wa-msg-dropdown" toggle-class="wa-msg-dots-btn">
                      <template #button-content><i class="bx bx-dots-vertical-rounded"></i></template>
                      <div class="wa-msg-detail-card">
                        <div class="d-flex align-items-center justify-content-between mb-2">
                          <span style="font-size:13px;font-weight:600;color:#1E293B;">Detalhes da mensagem</span>
                          <span :class="['wa-badge', m.status === 'read' ? 'active' : m.status === 'delivered' ? 'active' : m.status === 'failed' ? 'closed' : 'pending']" style="font-size:11px;">{{ { sent: 'Enviada', delivered: 'Entregue', read: 'Lida', failed: 'Falhou' }[m.status] || m.status }}</span>
                        </div>
                        <div class="d-flex" style="gap:16px;">
                          <div v-if="m.sent_at">
                            <div style="font-size:11px;color:#94A3B8;">Recebida</div>
                            <div style="font-size:12px;color:#1E293B;font-weight:500;">{{ $moment(m.sent_at).format('DD/MM/YYYY HH:mm:ss') }}</div>
                          </div>
                          <div v-if="m.delivered_at || m.sent_at">
                            <div style="font-size:11px;color:#94A3B8;">Processada</div>
                            <div style="font-size:12px;color:#1E293B;font-weight:500;">{{ $moment(m.delivered_at || m.sent_at).format('DD/MM/YYYY HH:mm:ss') }}</div>
                          </div>
                        </div>
                      </div>
                    </b-dropdown>
                    </div>
                  </div>
                </template>
              </template>
            </template>
          </div>

          <!-- Input -->
          <div :class="['wa-input-area', { 'no-padding': !sActive && conv.assigned_admin_id }]" style="position: relative;">
            <!-- Sem atendente: botão assumir -->
            <div v-if="!conv.assigned_admin_id" class="wa-input-row" style="border-top: 1px solid var(--mist);">
              <textarea disabled placeholder="Assuma a conversa para iniciar o atendimento..." rows="1"></textarea>
              <button class="wa-assume-btn" @click="doAssumeSelf"><i class="bx bx-user-check mr-1"></i> Assumir</button>
            </div>
            <!-- Com atendente, mas sem sessão ativa: banner envie template -->
            <div v-else-if="!sActive" class="wa-expired-banner">
              <span class="wa-expired-badge">Atenção</span>
              Envie um modelo de mensagem para iniciar ou reiniciar o atendimento com <strong>{{ dispName(conv) }}</strong>.
              <button class="btn btn-sm btn-outline-primary ml-2" @click="showTplModal = true"><i class="bx bx-file mr-1"></i>Enviar Template</button>
            </div>
            <!-- Sessão ativa: input completo -->
            <template v-else>
              <div v-if="selFile" class="wa-file-bar">
                <img v-if="isImg" :src="filePrev" />
                <i v-else class="bx bx-file" style="font-size: 20px; color: var(--accent);"></i>
                <span class="fname">{{ selFile.name }}</span>
                <button class="rm" @click="clearFile"><i class="bx bx-x"></i></button>
              </div>
              <div v-if="recordedAudioUrl" class="wa-audio-preview">
                <audio :src="recordedAudioUrl" controls class="wa-audio-player"></audio>
                <span class="wa-audio-time">{{ recTime }}</span>
                <button class="wa-audio-btn delete" title="Descartar" @click="clearAudio"><i class="bx bx-trash"></i></button>
                <button class="wa-audio-btn send" title="Enviar" @click="sendRecordedAudio"><i class="bx bx-send"></i></button>
              </div>
              <div v-if="!recordedAudioUrl" class="wa-input-tools">
                <button class="wa-tool-btn" title="Emoji" @click="showEmoji = !showEmoji"><i class="bx bx-smile"></i></button>
                <label class="wa-tool-btn" style="cursor:pointer;margin:0;" title="Anexar arquivo">
                  <i class="bx bx-paperclip"></i>
                  <input type="file" class="d-none" ref="fileIn" accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt" @change="onFile" />
                </label>
                <label class="wa-tool-btn" style="cursor:pointer;margin:0;" title="Enviar imagem">
                  <i class="bx bx-image"></i>
                  <input type="file" class="d-none" accept="image/*" @change="onFile" />
                </label>
                <label class="wa-tool-btn" style="cursor:pointer;margin:0;" title="Enviar documento">
                  <i class="bx bx-file-blank"></i>
                  <input type="file" class="d-none" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv" @change="onFile" />
                </label>
                <button class="wa-tool-btn" :class="{ 'recording': isRecording }" title="Gravar áudio" @click="toggleRecording">
                  <i :class="isRecording ? 'bx bx-stop' : 'bx bx-microphone'"></i>
                </button>
                <button class="wa-tpl-btn" @click="showTplModal = true"><i class="bx bx-file"></i> Templates</button>
                <div style="flex:1;"></div>
                <span v-if="isRecording" class="wa-recording-indicator">
                  <span class="rec-dot"></span> Gravando... {{ recTime }}
                </span>
              </div>
              <div v-if="!recordedAudioUrl" class="wa-input-row">
                <textarea v-model="msgText" placeholder="Digite uma mensagem..." @keydown.enter.exact.prevent="doSendText" rows="1"></textarea>
                <button class="wa-send-btn" :disabled="!canSend && !isRecording" @click="isRecording ? stopRecording() : doSendText()"><i :class="isRecording ? 'bx bx-send' : 'mdi mdi-send'"></i></button>
              </div>
              <div v-if="showEmoji" class="wa-emoji-container">
                <VEmojiPicker @select="onEmoji" />
              </div>
            </template>
          </div>
        </template>
      </div>

      <!-- ═══ DETAIL ═══ -->
      <div v-if="conv" class="wa-detail">
        <div class="wa-detail-head">
          <h3>Detalhes do Contato</h3>
          <button class="wa-sidebar-icon-btn" style="width:28px;height:28px;font-size:14px;" @click="showDetail = false"><i class="bx bx-x"></i></button>
        </div>
        <div class="wa-detail-card">
          <div class="wa-avatar lg" style="margin: 0 auto 12px;">{{ initials(conv) }}</div>
          <p class="d-name">{{ dispName(conv) }}</p>
          <p class="d-phone">{{ fmtPhone(conv.phone) }}</p>
          <p v-if="conv.contact" class="d-email">{{ conv.contact.email }}</p>
          <div class="wa-detail-actions">
            <button class="wa-detail-action-btn" title="WhatsApp" @click="openWA"><i class="bx bxl-whatsapp"></i></button>
            <button v-if="conv.contact" class="wa-detail-action-btn" title="Ver Participante" @click="goParticipant"><i class="bx bx-user"></i></button>
            <button class="wa-detail-action-btn" title="Editar" @click="showTransfer = true"><i class="bx bx-transfer"></i></button>
          </div>
        </div>

        <!-- Detail Tabs -->
        <b-tabs class="wa-detail-tabs" nav-class="nav-tabs" content-class="" small lazy>
          <!-- Tab: Info -->
          <b-tab title="Info" active>
            <div class="wa-detail-section">
              <div class="wa-section-title">Atendente <button class="edit-link" @click="showTransfer = true">Editar</button></div>
              <div v-if="conv.assigned_admin" class="wa-agent-card">
                <div class="wa-avatar" style="width:28px;height:28px;font-size:11px;border-radius:5px;">{{ nameInit(conv.assigned_admin.name) }}</div>
                <div><div class="a-name">{{ conv.assigned_admin.name }}</div></div>
              </div>
              <div v-else style="font-size:12px;color:#94A3B8;">Nenhum atendente</div>
            </div>
            <div v-if="conv.contact" class="wa-detail-section">
              <div class="wa-section-title">Participante</div>
              <div class="wa-info-row"><span class="lbl">Nome</span><span class="val">{{ conv.contact.name }}</span></div>
              <div class="wa-info-row"><span class="lbl">Email</span><span class="val">{{ conv.contact.email }}</span></div>
              <div class="wa-info-row"><span class="lbl">Telefone</span><span class="val">{{ conv.contact.phone }}</span></div>
              <div class="wa-info-row">
                <span class="lbl">Status</span>
                <span class="val"><span :class="['wa-badge', pStatusCls(conv.contact.status)]">{{ pStatusLbl(conv.contact.status, conv.contact.ref_status_name) }}</span></span>
              </div>
            </div>
            <div class="wa-detail-section">
              <div class="wa-section-title">Conversa</div>
              <div class="wa-status-cards">
                <div class="wa-status-card">
                  <span :class="['dot', sActive ? 'green' : 'gray']"></span>
                  <div><div class="s-label">Janela 24h</div><div class="s-value">{{ sActive ? 'Ativa' : 'Expirada' }}</div></div>
                </div>
                <div class="wa-status-card">
                  <span :class="['dot', conv.status==='active' ? 'green' : conv.status==='closed' ? 'gray' : 'yellow']"></span>
                  <div><div class="s-label">Status</div><div class="s-value">{{ statusLbl(conv) }}</div></div>
                </div>
              </div>
            </div>
          </b-tab>
          <!-- Tab: Histórico -->
          <b-tab title="Histórico">
            <div class="wa-detail-section" style="border-bottom: none;">
              <div class="wa-section-title">Histórico da Conversa</div>
              <div v-if="conv" style="font-size: 12px;">
                <div class="wa-log-item" v-if="conv.created_at">
                  <div class="log-time">{{ $moment(conv.created_at).format('DD/MM/YYYY HH:mm') }}</div>
                  <div class="log-text">Conversa criada</div>
                </div>
                <div class="wa-log-item" v-if="conv.assigned_admin">
                  <div class="log-text"><span class="log-operator">{{ conv.assigned_admin.name }}</span> atribuído como atendente</div>
                </div>
                <div class="wa-log-item" v-if="conv.status === 'closed'">
                  <div class="log-text">Conversa concluída</div>
                </div>
                <div class="wa-log-item" v-if="conv.archived_at">
                  <div class="log-time">{{ $moment(conv.archived_at).format('DD/MM/YYYY HH:mm') }}</div>
                  <div class="log-text">Conversa arquivada</div>
                </div>
              </div>
            </div>
          </b-tab>
          <!-- Tab: Anexos -->
          <b-tab title="Anexos">
            <div class="wa-detail-section" style="border-bottom: none;">
              <div class="wa-section-title">Mídias da Conversa</div>
              <div v-if="mediaMessages.length === 0" style="font-size: 12px; color: #94A3B8; text-align: center; padding: 20px 0;">Nenhuma mídia</div>
              <div v-for="mm in mediaMessages" :key="mm.id" style="margin-bottom: 8px;">
                <a v-if="mm.message_type === 'image'" :href="mediaUrl(mm)" target="_blank">
                  <img :src="mediaUrl(mm)" style="width: 100%; border-radius: 6px;" />
                </a>
                <a v-else :href="mediaUrl(mm)" target="_blank" class="wa-doc-bubble" style="width: 100%;">
                  <div class="doc-ext">{{ fileExt(mm.media_filename) }}</div>
                  <div><div class="doc-name">{{ mm.media_filename || 'Arquivo' }}</div></div>
                </a>
              </div>
            </div>
          </b-tab>
        </b-tabs>
      </div>
    </div>

    <!-- Message Info Sidebar -->
    <b-sidebar id="msg-info-sidebar" right shadow backdrop bg-variant="white" v-model="showMsgInfo" width="380px">
      <div class="px-3 py-2" v-if="msgInfoData">
        <h6 class="mb-4" style="font-size: 14px; font-weight: 700;">Dados da Mensagem</h6>

        <div class="mb-3 p-3 rounded" style="background: #FAFBFC; border: 1px solid #E8ECF0;">
          <div style="font-size: 13px; line-height: 1.5;" v-html="fmtMsg(msgInfoData.content || msgInfoData.body || '')"></div>
        </div>

        <div class="mb-2">
          <div class="d-flex align-items-center mb-3" v-if="msgInfoData.sent_at">
            <div style="width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; margin-right: 10px;" :style="{ background: '#ECFDF5' }">
              <i class="bx bx-check" style="font-size: 16px; color: #10B981;"></i>
            </div>
            <div>
              <div style="font-size: 12px; color: #94A3B8; font-weight: 500;">Enviada</div>
              <div style="font-size: 13px; color: #1E293B; font-weight: 500;">{{ $moment(msgInfoData.sent_at).format('DD/MM/YYYY [às] HH:mm:ss') }}</div>
            </div>
          </div>

          <div class="d-flex align-items-center mb-3" v-if="msgInfoData.delivered_at">
            <div style="width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; margin-right: 10px;" :style="{ background: '#EEF0FD' }">
              <i class="bx bx-check-double" style="font-size: 16px; color: #556ee6;"></i>
            </div>
            <div>
              <div style="font-size: 12px; color: #94A3B8; font-weight: 500;">Entregue</div>
              <div style="font-size: 13px; color: #1E293B; font-weight: 500;">{{ $moment(msgInfoData.delivered_at).format('DD/MM/YYYY [às] HH:mm:ss') }}</div>
            </div>
          </div>

          <div class="d-flex align-items-center mb-3" v-if="msgInfoData.read_at">
            <div style="width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; margin-right: 10px;" :style="{ background: '#E8F8EE' }">
              <i class="bx bx-check-double" style="font-size: 16px; color: #25D366;"></i>
            </div>
            <div>
              <div style="font-size: 12px; color: #94A3B8; font-weight: 500;">Lida</div>
              <div style="font-size: 13px; color: #1E293B; font-weight: 500;">{{ $moment(msgInfoData.read_at).format('DD/MM/YYYY [às] HH:mm:ss') }}</div>
            </div>
          </div>

          <div class="d-flex align-items-center mb-3" v-if="msgInfoData.failed_at">
            <div style="width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; margin-right: 10px;" :style="{ background: '#FEF2F2' }">
              <i class="bx bx-error-alt" style="font-size: 16px; color: #EF4444;"></i>
            </div>
            <div>
              <div style="font-size: 12px; color: #94A3B8; font-weight: 500;">Falhou</div>
              <div style="font-size: 13px; color: #1E293B; font-weight: 500;">{{ $moment(msgInfoData.failed_at).format('DD/MM/YYYY [às] HH:mm:ss') }}</div>
            </div>
          </div>
        </div>

        <div style="border-top: 1px solid #E8ECF0; padding-top: 12px;">
          <div class="d-flex justify-content-between mb-2" style="font-size: 12px;">
            <span style="color: #94A3B8;">Tipo</span>
            <span style="color: #1E293B; font-weight: 500;">{{ msgInfoData.message_type }}</span>
          </div>
          <div class="d-flex justify-content-between mb-2" style="font-size: 12px;">
            <span style="color: #94A3B8;">Direção</span>
            <span style="color: #1E293B; font-weight: 500;">{{ msgInfoData.direction === 'outbound' ? 'Enviada' : 'Recebida' }}</span>
          </div>
          <div class="d-flex justify-content-between mb-2" style="font-size: 12px;">
            <span style="color: #94A3B8;">Status</span>
            <span :class="['wa-badge', msgInfoData.status === 'read' ? 'active' : msgInfoData.status === 'failed' ? 'closed' : 'pending']">{{ msgInfoData.status }}</span>
          </div>
          <div v-if="msgInfoData.wa_id" class="d-flex justify-content-between mb-2" style="font-size: 12px;">
            <span style="color: #94A3B8;">WA ID</span>
            <span style="color: #1E293B; font-size: 10px; font-family: monospace; max-width: 200px; overflow: hidden; text-overflow: ellipsis;">{{ msgInfoData.wa_id }}</span>
          </div>
          <div v-if="msgInfoData.sender_name" class="d-flex justify-content-between mb-2" style="font-size: 12px;">
            <span style="color: #94A3B8;">Remetente</span>
            <span style="color: #1E293B; font-weight: 500;">{{ msgInfoData.sender_name }}</span>
          </div>
        </div>
      </div>
    </b-sidebar>

    <!-- ═══ MODALS ═══ -->

    <!-- Template Preview Sidebar -->
    <b-sidebar id="tpl-preview-sidebar" right shadow backdrop bg-variant="white" v-model="showTplPreview" width="400px" z-index="1060">
      <div class="px-3 py-2">
        <h6 class="mb-3">Preview do template</h6>
        <div v-if="selTpl" class="wa-preview-phone">
          <div class="wa-preview-header-bar">
            <div class="wa-preview-avatar"></div>
            <div class="wa-preview-name-bar"></div>
          </div>
          <div class="wa-preview-body">
            <div class="wa-preview-bubble">
              <img v-if="selTpl.header_image_url" :src="selTpl.header_image_url" style="width:100%;border-radius:6px 6px 0 0;display:block" />
              <video v-else-if="selTpl.header_video_url" :src="selTpl.header_video_url" controls style="width:100%;border-radius:6px 6px 0 0;display:block"></video>
              <div v-else-if="selTpl.header_document_url" style="padding:12px;background:#f4f5f7;border-radius:6px 6px 0 0;display:flex;align-items:center;gap:8px;">
                <i class="bx bx-file" style="font-size:20px;color:#556ee6;"></i>
                <a :href="selTpl.header_document_url" target="_blank" style="font-size:12px;">Ver documento</a>
              </div>
              <div class="wa-preview-content" v-html="tplPreviewBody(selTpl)"></div>
            </div>
          </div>
        </div>
      </div>
    </b-sidebar>

    <!-- Template Modal -->
    <b-modal v-model="showTplModal" title="Enviar Template" size="lg" :no-close-on-backdrop="true" hide-footer modal-class="wa-modal">
      <div class="mb-3">
        <div class="position-relative">
          <i class="bx bx-search" style="position:absolute;left:12px;top:50%;transform:translateY(-50%);color:#94A3B8;"></i>
          <input type="text" class="form-control" style="padding-left:36px;background:#FAFBFC;border-color:#E8ECF0;" placeholder="Buscar template..." v-model="tplSearch" />
        </div>
      </div>
      <div style="max-height: 45vh; overflow-y: auto;">
        <div
          v-for="t in filteredTpls"
          :key="t.name"
          :class="['d-flex align-items-start gap-3 p-3 mb-2 rounded', selTpl && selTpl.name === t.name ? 'border-primary bg-soft-primary' : '']"
          style="cursor:pointer;border:1.5px solid #E8ECF0;border-radius:10px !important;transition:all .15s;"
          @click="pickTpl(t)"
        >
          <div style="flex:1;min-width:0;">
            <div style="font-size:13px;font-weight:600;color:#1E293B;margin-bottom:2px;">{{ t.name }}</div>
            <div v-if="tplBody(t)" style="font-size:12px;color:#64748B;line-height:1.4;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;">{{ tplBody(t) }}</div>
            <div style="margin-top:4px;">
              <span v-if="tplHeaderFormat(t) === 'IMAGE'" class="badge badge-soft-info" style="font-size:10px;">Header com imagem</span>
              <span v-else-if="tplHeaderFormat(t) === 'VIDEO'" class="badge badge-soft-warning" style="font-size:10px;">Header com vídeo</span>
              <span v-else-if="tplHeaderFormat(t) === 'DOCUMENT'" class="badge badge-soft-secondary" style="font-size:10px;">Header com documento</span>
            </div>
          </div>
          <button
            type="button"
            style="flex-shrink:0;border:none;background:none;color:#94A3B8;padding:4px 6px;cursor:pointer;font-size:13px;"
            title="Ver preview"
            @click.stop="selTpl = t; showTplPreview = true"
          ><i class="bx bx-show"></i></button>
        </div>
        <div v-if="filteredTpls.length === 0" class="text-center p-4" style="color:#94A3B8;">Nenhum template</div>
      </div>
      <!-- IMAGE header -->
      <div v-if="selTpl && selTplImg" class="mt-3 pt-3" style="border-top:1px solid #E8ECF0;">
        <label style="font-size:12px;font-weight:600;color:#475569;">Imagem do Header</label>
        <div v-if="selTpl.header_image_url && !tplImgChanged" class="mt-2">
          <img :src="selTpl.header_image_url" style="max-height:120px;border-radius:6px;border:1px solid #e9ecef;cursor:pointer" @click="tplImgZoom = true" title="Ampliar" />
          <small class="text-muted d-block mt-1">Imagem padrão · <a href="#" @click.prevent="tplImgZoom = true">Ampliar</a></small>
          <button type="button" class="btn btn-sm btn-outline-secondary mt-1" @click="tplImgChanged = true"><i class="bx bx-image-add mr-1"></i> Trocar imagem</button>
          <b-modal v-model="tplImgZoom" title="Imagem do template" size="lg" hide-footer centered>
            <img :src="selTpl.header_image_url" style="width:100%;border-radius:6px" />
          </b-modal>
        </div>
        <div v-else class="mt-1">
          <input type="file" class="form-control form-control-sm" style="background:#FAFBFC;" accept="image/*" @change="tplHeaderFile = $event.target.files[0]" />
          <button v-if="selTpl.header_image_url" type="button" class="btn btn-sm btn-link p-0 mt-1" @click="tplImgChanged = false; tplHeaderFile = null">Usar imagem padrão</button>
        </div>
      </div>
      <!-- VIDEO header -->
      <div v-if="selTpl && selTplVideo" class="mt-3 pt-3" style="border-top:1px solid #E8ECF0;">
        <label style="font-size:12px;font-weight:600;color:#475569;">Vídeo do Header</label>
        <div v-if="selTpl.header_video_url && !tplImgChanged" class="mt-2">
          <video :src="selTpl.header_video_url" controls style="max-height:150px;border-radius:6px;border:1px solid #e9ecef;display:block"></video>
          <small class="text-muted d-block mt-1">Vídeo padrão do template</small>
          <button type="button" class="btn btn-sm btn-outline-secondary mt-1" @click="tplImgChanged = true"><i class="bx bx-video-plus mr-1"></i> Trocar vídeo</button>
        </div>
        <div v-else class="mt-1">
          <input type="file" class="form-control form-control-sm" style="background:#FAFBFC;" accept="video/mp4,video/3gp,video/*" @change="tplHeaderFile = $event.target.files[0]" />
          <button v-if="selTpl.header_video_url" type="button" class="btn btn-sm btn-link p-0 mt-1" @click="tplImgChanged = false; tplHeaderFile = null">Usar vídeo padrão</button>
        </div>
      </div>
      <!-- DOCUMENT header -->
      <div v-if="selTpl && selTplDocument" class="mt-3 pt-3" style="border-top:1px solid #E8ECF0;">
        <label style="font-size:12px;font-weight:600;color:#475569;">Documento do Header</label>
        <div v-if="selTpl.header_document_url && !tplImgChanged" class="mt-2">
          <a :href="selTpl.header_document_url" target="_blank" class="btn btn-sm btn-outline-secondary"><i class="bx bx-file mr-1"></i> Ver documento do template</a>
          <small class="text-muted d-block mt-1">Documento padrão do template</small>
          <button type="button" class="btn btn-sm btn-outline-secondary mt-1" @click="tplImgChanged = true"><i class="bx bx-upload mr-1"></i> Trocar documento</button>
        </div>
        <div v-else class="mt-1">
          <input type="file" class="form-control form-control-sm" style="background:#FAFBFC;" accept=".pdf,.doc,.docx,application/pdf" @change="tplHeaderFile = $event.target.files[0]" />
          <button v-if="selTpl.header_document_url" type="button" class="btn btn-sm btn-link p-0 mt-1" @click="tplImgChanged = false; tplHeaderFile = null">Usar documento padrão</button>
        </div>
      </div>
      <!-- Auto vars: info chips (read-only) -->
      <div v-if="tplAutoVars.length > 0" class="mt-3 pt-3" style="border-top:1px solid #E8ECF0;">
        <label style="font-size:12px;font-weight:600;color:#475569;margin-bottom:6px;display:block;">Variáveis automáticas</label>
        <div style="display:flex;flex-wrap:wrap;gap:6px;">
          <span v-for="v in tplAutoVars" :key="v.pos" style="background:#F0FDF4;border:1px solid #BBF7D0;border-radius:6px;padding:3px 8px;font-size:11px;color:#166534;">
            <span style="font-weight:600;font-family:monospace;" v-text="varTag(v.pos)"></span> → {{ v.label }}
          </span>
        </div>
      </div>
      <!-- Custom vars: editable inputs -->
      <div v-if="tplCustomParams.length > 0" class="mt-3 pt-3" style="border-top:1px solid #E8ECF0;">
        <label style="font-size:12px;font-weight:600;color:#475569;margin-bottom:6px;display:block;">Preencher variáveis</label>
        <div v-for="p in tplParams" :key="p.pos" class="mb-2">
          <label style="font-size:11px;color:#64748B;margin-bottom:2px;display:block;"><span v-text="varTag(p.pos)"></span> — variável personalizada</label>
          <input type="text" class="form-control form-control-sm" style="background:#FAFBFC;" v-model="p.value" placeholder="Digite o valor..." />
        </div>
      </div>

      <div class="d-flex justify-content-end mt-3 pt-3" style="border-top:1px solid #E8ECF0;gap:8px;">
        <button class="btn btn-light" @click="showTplModal = false; showTplPreview = false">Cancelar</button>
        <button class="btn btn-success" :disabled="!selTpl || (selTplImg && !tplHeaderFile && !selTpl.header_image_url) || (selTplVideo && !tplHeaderFile && !selTpl.header_video_url) || (selTplDocument && !tplHeaderFile && !selTpl.header_document_url)" @click="doSendTpl">
          <i class="bx bxl-whatsapp mr-1"></i> Enviar Template
        </button>
      </div>
    </b-modal>

    <!-- New Conversation Modal -->
    <b-modal v-model="showNewConv" title="Nova Conversa" :no-close-on-backdrop="true" hide-footer modal-class="wa-modal">
      <div class="mb-3">
        <label style="font-size:12px;font-weight:600;color:#475569;margin-bottom:6px;display:block;">Buscar participante</label>
        <div class="position-relative">
          <i class="bx bx-search" style="position:absolute;left:12px;top:50%;transform:translateY(-50%);color:#94A3B8;"></i>
          <input type="text" class="form-control" style="padding-left:36px;background:#FAFBFC;border-color:#E8ECF0;" placeholder="Buscar por nome..." v-model="pSearch" @input="searchParts" />
        </div>
        <div v-if="pResults.length" class="border rounded mt-1" style="max-height:150px;overflow-y:auto;">
          <div v-for="p in pResults" :key="p.id" :class="['p-2', nc.participant_id === p.id ? 'bg-soft-primary' : '']" style="cursor:pointer;font-size:13px;" @click="pickPart(p)">
            <strong>{{ p.name }}</strong> <small class="text-muted ml-1">{{ p.phone }}</small>
          </div>
        </div>
        <div v-if="nc.participant_id && nc.name" class="mt-2 p-2 rounded d-flex align-items-center" style="background:#FAFBFC;border:1px solid #E8ECF0;">
          <strong style="flex:1;font-size:13px;">{{ nc.name }} — {{ nc.phone }}</strong>
          <button style="border:none;background:none;color:#94A3B8;cursor:pointer;" @click="nc.participant_id=null;nc.name='';nc.phone=''"><i class="bx bx-x"></i></button>
        </div>
      </div>
      <div v-if="!nc.participant_id" style="border-top:1px solid #E8ECF0;padding-top:12px;">
        <p class="text-muted font-size-12 mb-3">Ou insira os dados de um novo contato:</p>
        <div class="mb-3">
          <label style="font-size:12px;font-weight:600;color:#475569;margin-bottom:6px;display:block;">Nome</label>
          <input type="text" class="form-control" style="background:#FAFBFC;border-color:#E8ECF0;" v-model="nc.name" placeholder="Nome do contato" />
        </div>
        <div class="mb-3">
          <label style="font-size:12px;font-weight:600;color:#475569;margin-bottom:6px;display:block;">Telefone</label>
          <input type="text" class="form-control" style="background:#FAFBFC;border-color:#E8ECF0;" v-model="nc.phone" placeholder="Ex: 11999999999" />
        </div>
      </div>
      <div class="d-flex justify-content-end mt-3 pt-3" style="border-top:1px solid #E8ECF0;gap:8px;">
        <button class="btn btn-light" @click="showNewConv = false">Cancelar</button>
        <button class="btn btn-success" :disabled="!nc.phone && !nc.participant_id" @click="doStartConv">
          <i class="bx bxl-whatsapp mr-1"></i> Iniciar Conversa
        </button>
      </div>
    </b-modal>

    <!-- Transfer Modal -->
    <b-modal v-model="showTransfer" title="Transferir Conversa" :no-close-on-backdrop="true" hide-footer modal-class="wa-modal">
      <div v-if="loadingOps" class="text-center p-4"><b-spinner small variant="primary"></b-spinner></div>
      <div v-else>
        <p style="font-size:13px;color:#64748B;margin-bottom:12px;">Selecione um operador:</p>
        <div v-for="op in operators" :key="op.id" :class="['d-flex align-items-center p-3 mb-2 rounded', selOp === op.id ? 'border-primary bg-soft-primary' : '']" style="cursor:pointer;border:1.5px solid #E8ECF0;border-radius:10px !important;" @click="selOp = op.id">
          <div class="wa-avatar" style="width:32px;height:32px;font-size:12px;border-radius:6px;margin-right:12px;">{{ nameInit(op.name) }}</div>
          <div style="font-size:13px;font-weight:600;color:#1E293B;">{{ op.name }}</div>
        </div>
        <div v-if="!operators.length" class="text-center p-4" style="color:#94A3B8;">Nenhum operador</div>
      </div>
      <div class="d-flex justify-content-end mt-3 pt-3" style="border-top:1px solid #E8ECF0;gap:8px;">
        <button class="btn btn-light" @click="showTransfer = false">Cancelar</button>
        <button class="btn btn-primary" :disabled="!selOp" @click="doAssign"><i class="bx bx-transfer mr-1"></i> Transferir</button>
      </div>
    </b-modal>
  </Layout>
</template>

<script>
import Layout from '@/components/layout/vertical'
import { mapGetters, mapActions } from 'vuex'
import { successMessage, errorMessage } from '@/components/alertsMessages'
import services from './services'
import { markAsRemoved } from './store/actions'
import moment from 'moment'
import { VEmojiPicker } from 'v-emoji-picker'

export default {
  name: 'WAMessenger',
  components: { Layout, VEmojiPicker },
  data () {
    return {
      selConvId: null,
      loadingConvs: false,
      loadingMsgs: false,
      sending: false,
      showDetail: false,
      showEmoji: false,
      searchQuery: '',
      searchTO: null,
      activeFilter: 'all',
      showingArchived: false,
      filters: [
        { label: 'Todos', value: 'all' },
        { label: 'Pendentes', value: 'pending' },
        { label: 'Meus', value: 'mine' }
      ],
      msgText: '',
      selFile: null,
      filePrev: null,
      showTplModal: false,
      showTplPreview: false,
      tplSearch: '',
      selTpl: null,
      selTplImg: false,
      selTplVideo: false,
      selTplDocument: false,
      tplHeaderFile: null,
      tplImgChanged: false,
      tplImgZoom: false,
      tplParams: [],
      showNewConv: false,
      nc: { name: '', phone: '', participant_id: null, template_name: '', template_lang: 'pt_BR' },
      pSearch: '',
      pResults: [],
      ncParams: [],
      ncNeedsImg: false,
      ncImgFile: null,
      showTransfer: false,
      operators: [],
      loadingOps: false,
      selOp: null,
      nowTs: moment(),
      tickInterval: null,
      showMsgInfo: false,
      msgInfoData: null,
      // Audio recording
      isRecording: false,
      mediaRecorder: null,
      audioChunks: [],
      recTime: '00:00',
      recInterval: null,
      recSeconds: 0,
      recordedAudio: null,
      recordedAudioUrl: null,
      pusherCh: null,
      hasMoreMessages: true,
      loadingMore: false
    }
  },
  computed: {
    ...mapGetters('messenger', {
      conversations: 'CONVERSATIONS',
      conv: 'CONVERSATION',
      messages: 'MESSAGES',
      templates: 'TEMPLATES',
      sActive: 'SESSION_ACTIVE'
    }),
    countdown () {
      if (!this.conv || !this.conv.session_expires_at || !this.sActive) return ''
      const d = moment.duration(moment(this.conv.session_expires_at).diff(this.nowTs))
      if (d.asSeconds() <= 0) return '00:00:00'
      return `${String(Math.floor(d.asHours())).padStart(2, '0')}:${String(d.minutes()).padStart(2, '0')}:${String(d.seconds()).padStart(2, '0')}`
    },
    canSend () {
      if (this.sending) return false
      if (this.selFile) return true
      return this.msgText.trim().length > 0 && this.sActive
    },
    isImg () { return this.selFile && this.selFile.type.startsWith('image/') },
    filteredTpls () {
      if (!this.tplSearch) return this.templates
      const q = this.tplSearch.toLowerCase()
      return this.templates.filter(t => t.name.toLowerCase().includes(q))
    },
    tplVariableLabels () {
      return {
        'participant.name': 'Nome do participante',
        'participant.email': 'E-mail',
        'participant.phone': 'Telefone',
        'participant.badge_name': 'Nome no crachá',
        'participant.confirmation_code': 'Código de confirmação',
        'participant.group_name': 'Grupo',
        'participant.city': 'Cidade',
        'participant.state': 'Estado',
        'event.name': 'Nome do evento',
        'event.date': 'Data do evento',
        'event.end_date': 'Data de encerramento',
        'event.location': 'Local do evento',
        'event.address': 'Endereço do evento',
        'attachment.type': 'Tipo de anexo',
        'custom': 'Personalizado'
      }
    },
    tplAutoVars () {
      if (!this.selTpl || !this.selTpl.variables || !Object.keys(this.selTpl.variables).length) return []
      const labels = this.tplVariableLabels
      return Object.entries(this.selTpl.variables)
        .filter(([, key]) => key !== 'custom')
        .map(([pos, key]) => ({ pos: parseInt(pos), label: labels[key] || key }))
        .sort((a, b) => a.pos - b.pos)
    },
    tplCustomParams () {
      if (!this.selTpl || !this.selTpl.variables || !Object.keys(this.selTpl.variables).length) return []
      return Object.entries(this.selTpl.variables)
        .filter(([, key]) => key === 'custom')
        .map(([pos]) => parseInt(pos))
    },
    groupedMsgs () {
      const g = {}
      this.messages.forEach(m => {
        const d = m.sent_at ? moment(m.sent_at).format('DD/MM/YYYY') : ''
        const today = moment().format('DD/MM/YYYY')
        const yest = moment().subtract(1, 'day').format('DD/MM/YYYY')
        let lbl = d
        if (d === today) lbl = 'Hoje'
        else if (d === yest) lbl = 'Ontem'
        if (!g[lbl]) g[lbl] = []
        g[lbl].push(m)
      })
      return g
    },
    mediaMessages () {
      return this.messages.filter(m => ['image', 'document', 'video', 'audio'].includes(m.message_type) && m.media_url)
    }
  },
  methods: {
    ...mapActions('messenger', ['getConversations', 'selectConversation', 'loadMessages', 'sendMessage', 'sendMediaMessage', 'startNewConversation', 'markConversationRead', 'closeConversation', 'loadTemplates', 'receiveNewMessage', 'receiveStatusUpdate']),

    // Helpers
    dispName (c) { return c.contact?.name || c.contact_name || c.phone || '?' },
    initials (c) { const n = this.dispName(c); if (!n) return '?'; const p = n.split(' '); return p.length > 1 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : n[0].toUpperCase() },
    nameInit (n) { if (!n) return '?'; const p = n.split(' '); return p.length > 1 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : n[0].toUpperCase() },
    fmtTime (d) { if (!d) return ''; const m = moment(d); if (m.isSame(moment(), 'day')) return m.format('HH:mm'); if (m.isSame(moment().subtract(1, 'day'), 'day')) return 'Ontem'; return m.format('DD/MM') },
    fmtMsgTime (d) { return d ? moment(d).format('HH:mm') : '' },
    fmtMsg (t) { if (!t) return ''; return t.replace(/\r\n/g, '<br>').replace(/\n/g, '<br>').replace(/\*(.*?)\*/g, '<strong>$1</strong>').replace(/_(.*?)_/g, '<em>$1</em>') },
    fmtPhone (p) { if (!p) return ''; const c = p.replace(/\D/g, ''); if (c.length === 13) return `+${c.slice(0, 2)} (${c.slice(2, 4)}) ${c.slice(4, 9)}-${c.slice(9)}`; if (c.length >= 11) return `(${c.slice(0, 2)}) ${c.slice(2, 7)}-${c.slice(7)}`; return p },
    mediaUrl (m) { if (!m.media_url) return null; if (m.media_url.startsWith('http')) return m.media_url; return (process.env.VUE_APP_STORAGE_URL || '') + '/' + m.media_url },
    fileExt (f) { return f ? f.split('.').pop().toUpperCase().slice(0, 4) : 'DOC' },
    hasSession (c) { return c.session_expires_at && moment().isBefore(moment(c.session_expires_at)) },
    statusCls (c) { if (c.status === 'bot') return 'bot'; if (c.status === 'closed') return 'closed'; if (c.archived_at) return 'closed'; if (!c.assigned_admin_id) return 'pending'; return 'active' },
    statusLbl (c) { if (c.status === 'bot') return 'Bot IA'; if (c.status === 'closed') return 'Concluído'; if (c.archived_at) return 'Arquivado'; if (!c.assigned_admin_id) return 'Pendente'; return 'Ativo' },
    // System divider helpers
    sysDividerType (m) {
      const c = (m.content || '').toLowerCase()
      if (c.includes('transferi')) return 'transfer'
      if (c.includes('assumiu')) return 'assign'
      if (c.includes('arquivou')) return 'archive'
      if (c.includes('concluiu') || c.includes('encerrou')) return 'close'
      if (c.includes('assistente ia')) return 'bot-handoff'
      return 'info'
    },
    sysDividerIcon (m) {
      const t = this.sysDividerType(m)
      return { transfer: 'bx bx-transfer', assign: 'bx bx-right-arrow-alt', archive: 'bx bx-archive-in', close: 'bx bx-check-circle', 'bot-handoff': 'bx bx-bot' }[t] || 'bx bx-info-circle'
    },
    sysDividerText (m) {
      const time = this.$moment(m.sent_at).format('DD/MM/YYYY [às] HH:mm')
      return m.content + ' em <strong>' + time + '</strong>'
    },
    pStatusCls (s) { return { 0: 'ps-dark', 1: 'ps-primary', 2: 'ps-warning', 3: 'ps-success', 4: 'ps-danger', 5: 'ps-secondary' }[s] || 'ps-dark' },
    pStatusLbl (s, refStatusName) {
      const labels = { 0: 'A Definir', 1: 'Pendente', 2: 'Em andamento', 3: 'Confirmado', 4: 'Declinado', 5: 'Lista de Espera' }
      const base = labels[s] || 'A Definir'
      if (refStatusName) return base + ' - ' + refStatusName
      return base
    },
    varTag (n) { return '{{' + n + '}}' },
    tplBody (t) { const b = (t.components || []).find(c => c.type === 'BODY'); return b ? b.text : '' },
    tplPreviewBody (t) {
      const comps = t.components || []
      let parts = []
      comps.forEach(c => {
        if (c.type === 'HEADER' && c.format === 'TEXT') parts.push('<strong>' + (c.text || '') + '</strong>')
        if (c.type === 'BODY') {
          let text = (c.text || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
          text = text.replace(/\*(.*?)\*/g, '<strong>$1</strong>')
          text = text.replace(/_(.*?)_/g, '<em>$1</em>')
          text = text.replace(/~(.*?)~/g, '<del>$1</del>')
          text = text.replace(/\{\{(\d+)\}\}/g, '<span style="background:rgba(85,110,230,.1);color:#556ee6;padding:1px 4px;border-radius:3px;font-size:11px;font-weight:600">{{$1}}</span>')
          text = text.replace(/\r?\n/g, '<br>')
          parts.push(text)
        }
        if (c.type === 'FOOTER') parts.push('<span style="color:#7A8BA7;font-size:10.5px">' + (c.text || '') + '</span>')
      })
      return parts.join('<br>')
    },
    tplHasImg (t) { const h = (t.components || []).find(c => c.type === 'HEADER'); return h && h.format === 'IMAGE' },
    tplHeaderFormat (t) { const h = (t.components || []).find(c => c.type === 'HEADER'); return h ? h.format : null },
    checkIcon (m) { return m.status === 'read' ? 'bx bx-check-double' : m.status === 'delivered' ? 'bx bx-check-double' : m.status === 'sent' ? 'bx bx-check' : m.status === 'failed' ? 'bx bx-error-alt' : 'bx bx-check' },
    scroll () { this.$nextTick(() => { const e = this.$refs.msgBox; if (e) e.scrollTop = e.scrollHeight }) },
    convPreview (c) {
      if (!c.last_message_preview) return 'Sem mensagens'
      if (c.last_message_direction === 'outbound' && c.last_message_operator_name) {
        return `${c.last_message_operator_name}: ${c.last_message_preview}`
      }
      return c.last_message_preview
    },
    showSessionExpiredSep (grp, idx) {
      if (idx === 0) return false
      const prev = grp[idx - 1]
      const curr = grp[idx]
      if (prev.direction !== 'inbound' || curr.direction !== 'outbound') return false
      if (curr.message_type !== 'template') return false
      const diff = moment(curr.sent_at).diff(moment(prev.sent_at), 'hours')
      return diff >= 24
    },
    fmtSessionExpiry (grp, idx) {
      const prev = grp[idx - 1]
      if (!prev || !prev.sent_at) return ''
      return moment(prev.sent_at).add(24, 'hours').format('HH:mm') + ' do dia ' + moment(prev.sent_at).add(24, 'hours').format('DD/MM/YYYY')
    },

    // Data
    async fetchConvs (p = {}) {
      this.loadingConvs = true
      try { await this.getConversations({ ...p, filter: this.showingArchived ? 'archived' : this.activeFilter, search: this.searchQuery }) } catch (e) { errorMessage('Erro ao carregar conversas') } finally { this.loadingConvs = false }
    },
    async openConv (c) {
      this.selConvId = c.id
      this.loadingMsgs = true
      this.hasMoreMessages = true
      this.loadingMore = false
      // Zerar badge imediatamente ao clicar (mutação direta + store)
      this.$set(c, 'unread_count', 0)
      this.$store.commit('messenger/MESSENGER/UPDATE_CONVERSATION', { id: c.id, unread_count: 0 })
      try {
        await this.selectConversation(c.id)
        await this.loadMessages({ id: c.id, params: { limit: 20 } })
        this.markConversationRead(c.id).catch(() => {})
        this.$nextTick(() => { this.scroll() })
      } catch (e) {
        // Conversa não encontrada (fantasma do Pusher) — remover da lista e recarregar
        this.$store.commit('messenger/MESSENGER/REMOVE_CONVERSATION', c.id)
        this.selConvId = null
        this.$store.commit('messenger/MESSENGER/SET_CONVERSATION', null)
        this.$store.commit('messenger/MESSENGER/SET_MESSAGES', [])
        this.fetchConvs()
      } finally { this.loadingMsgs = false }
    },
    async loadOlderMessages () {
      if (this.loadingMore || !this.hasMoreMessages || !this.selConvId) return
      if (!this.messages.length) { this.hasMoreMessages = false; return }
      // Encontrar a mensagem mais antiga pelo created_at (messages[0] pode não ser a mais antiga)
      var oldestMsg = this.messages.reduce(function (oldest, current) { return new Date(oldest.created_at) < new Date(current.created_at) ? oldest : current })
      this.loadingMore = true
      try {
        var msgBox = this.$refs.msgBox
        var prevScrollHeight = msgBox ? msgBox.scrollHeight : 0
        var prevScrollTop = msgBox ? msgBox.scrollTop : 0
        var countBefore = this.messages.length
        var res = await services.getMessages(this.selConvId, { limit: 40, before: oldestMsg.id })
        var responseData = res.data
        var olderMsgs = []
        if (Array.isArray(responseData)) {
          olderMsgs = responseData
        } else if (responseData && Array.isArray(responseData.data)) {
          olderMsgs = responseData.data
        }
        if (olderMsgs.length === 0) {
          this.hasMoreMessages = false
          return
        }
        this.$store.commit('messenger/MESSENGER/PREPEND_MESSAGES', olderMsgs)
        // Se nada novo foi adicionado ao state, não há mais mensagens reais
        if (this.messages.length === countBefore) {
          this.hasMoreMessages = false
          return
        }
        // Se retornou menos que o limit, não há mais
        if (olderMsgs.length < 40) {
          this.hasMoreMessages = false
        }
        this.$nextTick(function () {
          if (msgBox) {
            var newScrollHeight = msgBox.scrollHeight
            msgBox.scrollTop = prevScrollTop + (newScrollHeight - prevScrollHeight)
          }
        })
      } catch (e) {
        this.hasMoreMessages = false
      } finally {
        this.loadingMore = false
      }
    },
    onMessagesScroll () {
      if (this.loadingMore || !this.hasMoreMessages) return
      var el = this.$refs.msgBox
      if (el && el.scrollTop < 80) {
        this.loadOlderMessages()
      }
    },
    onSearch () { clearTimeout(this.searchTO); this.searchTO = setTimeout(() => this.fetchConvs(), 400) },
    setFilter (f) { this.activeFilter = f; this.showingArchived = false; this.fetchConvs() },
    toggleArchived () { this.showingArchived = !this.showingArchived; this.fetchConvs() },

    // Send
    async doSendText () {
      if (this.selFile) return this.doSendMedia()
      if (!this.msgText.trim() || !this.sActive || !this.selConvId) return
      this.sending = true
      try { await this.sendMessage({ id: this.selConvId, payload: { type: 'text', content: this.msgText.trim() } }); this.msgText = ''; this.scroll(); this.refreshConvInSidebar() } catch (e) { errorMessage(e.response?.data?.error || 'Erro') } finally { this.sending = false }
    },
    onFile (e) { const f = e.target.files[0]; if (!f) return; this.selFile = f; if (f.type.startsWith('image/')) { const r = new FileReader(); r.onload = ev => { this.filePrev = ev.target.result }; r.readAsDataURL(f) } else { this.filePrev = 'file' } },
    clearFile () { this.selFile = null; this.filePrev = null; if (this.$refs.fileIn) this.$refs.fileIn.value = '' },
    async doSendMedia () {
      if (!this.selFile || !this.selConvId) return; this.sending = true
      try {
        const fd = new FormData(); fd.append('file', this.selFile)
        const tp = this.selFile.type.startsWith('image/') ? 'image' : this.selFile.type.startsWith('video/') ? 'video' : this.selFile.type.startsWith('audio/') ? 'audio' : 'document'
        fd.append('type', tp); if (this.msgText.trim()) fd.append('caption', this.msgText.trim())
        await this.sendMediaMessage({ id: this.selConvId, formData: fd }); this.msgText = ''; this.clearFile(); this.scroll(); this.refreshConvInSidebar()
      } catch (e) { errorMessage(e.response?.data?.error || 'Erro') } finally { this.sending = false }
    },
    onEmoji (e) { this.msgText += e.data || e.emoji || e; this.showEmoji = false },
    openMsgInfo (m) { this.msgInfoData = m; this.showMsgInfo = true },

    // Audio recording
    async toggleRecording () {
      if (this.isRecording) {
        this.stopRecording()
      } else {
        this.startRecording()
      }
    },
    async startRecording () {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        // WhatsApp accepts: ogg/opus, mp4, aac, mp3, amr
        // Priority: ogg/opus > mp4 > webm (webm needs server conversion)
        var mimeType = 'audio/webm'
        var recExt = 'webm'
        if (MediaRecorder.isTypeSupported('audio/ogg; codecs=opus')) {
          mimeType = 'audio/ogg; codecs=opus'
          recExt = 'ogg'
        } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4'
          recExt = 'm4a'
        } else if (MediaRecorder.isTypeSupported('audio/webm; codecs=opus')) {
          mimeType = 'audio/webm; codecs=opus'
          recExt = 'webm'
        }
        this.mediaRecorder = new MediaRecorder(stream, { mimeType: mimeType })
        this.audioChunks = []
        this.mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) this.audioChunks.push(e.data) }
        this.mediaRecorder.onstop = () => {
          stream.getTracks().forEach(t => t.stop())
          const blob = new Blob(this.audioChunks, { type: mimeType })
          this.recordedAudio = new File([blob], `audio_${Date.now()}.${recExt}`, { type: mimeType })
          this.recordedAudioUrl = URL.createObjectURL(blob)
        }
        this.mediaRecorder.start()
        this.isRecording = true
        this.recSeconds = 0
        this.recTime = '00:00'
        this.recInterval = setInterval(() => {
          this.recSeconds++
          const m = String(Math.floor(this.recSeconds / 60)).padStart(2, '0')
          const s = String(this.recSeconds % 60).padStart(2, '0')
          this.recTime = `${m}:${s}`
        }, 1000)
      } catch (e) {
        errorMessage('Não foi possível acessar o microfone')
      }
    },
    stopRecording () {
      if (this.mediaRecorder && this.isRecording) {
        this.mediaRecorder.stop()
        this.isRecording = false
        clearInterval(this.recInterval)
      }
    },
    clearAudio () {
      if (this.recordedAudioUrl) URL.revokeObjectURL(this.recordedAudioUrl)
      this.recordedAudio = null
      this.recordedAudioUrl = null
      this.recTime = '00:00'
      this.recSeconds = 0
    },
    async sendRecordedAudio () {
      if (!this.recordedAudio) return
      await this.sendAudioFile(this.recordedAudio)
      this.clearAudio()
    },
    async sendAudioFile (file) {
      if (!this.selConvId) return
      this.sending = true
      try {
        const fd = new FormData()
        fd.append('file', file)
        fd.append('type', 'audio')
        await this.sendMediaMessage({ id: this.selConvId, formData: fd })
        this.scroll()
        this.refreshConvInSidebar()
        successMessage('Áudio enviado')
      } catch (e) {
        errorMessage('Erro ao enviar áudio')
      } finally {
        this.sending = false
      }
    },

    // Template
    pickTpl (t) {
      this.selTpl = t
      const fmt = this.tplHeaderFormat(t)
      this.selTplImg = fmt === 'IMAGE'
      this.selTplVideo = fmt === 'VIDEO'
      this.selTplDocument = fmt === 'DOCUMENT'
      this.tplHeaderFile = null
      this.tplImgChanged = false
      this.tplParams = []

      const variables = t.variables && Object.keys(t.variables).length ? t.variables : null

      if (variables) {
        // New system: only create params for 'custom' positions
        this.tplParams = Object.entries(variables)
          .filter(([, key]) => key === 'custom')
          .map(([pos]) => ({ pos: parseInt(pos), value: '' }))
      } else {
        // Legacy: create a param for every {{N}} found in body
        const b = (t.components || []).find(c => c.type === 'BODY')
        if (b && b.text) {
          const ms = b.text.match(/\{\{(\d+)\}\}/g)
          this.tplParams = (ms || []).map((m, i) => ({ pos: i + 1, value: '' }))
        }
      }
    },
    async doSendTpl () {
      if (!this.selTpl || !this.selConvId) return
      this.sending = true
      try {
        const comps = []
        const variables = this.selTpl.variables && Object.keys(this.selTpl.variables).length
          ? this.selTpl.variables
          : null

        if (variables) {
          // New system: build parameters array with custom values at their correct positions
          // Auto vars are sent as empty string — backend resolver fills them in
          const maxPos = Math.max(...Object.keys(variables).map(Number))
          const parameters = []
          for (let i = 1; i <= maxPos; i++) {
            const key = variables[String(i)]
            if (key === 'custom') {
              const customParam = this.tplParams.find(p => p.pos === i)
              parameters.push({ type: 'text', text: customParam ? customParam.value : '' })
            } else {
              parameters.push({ type: 'text', text: '' }) // backend resolves this
            }
          }
          if (parameters.length) comps.push({ type: 'body', parameters })
        } else if (this.tplParams.length) {
          // Legacy: send all params as flat array
          comps.push({
            type: 'body',
            parameters: this.tplParams.map((p, i) => ({
              type: 'text',
              text: p.value || '{{' + (i + 1) + '}}'
            }))
          })
        }

        const hasFileUpload = this.tplHeaderFile && this.tplImgChanged
        const hasDefaultImage = this.selTplImg && this.selTpl.header_image_url && !this.tplImgChanged
        const hasDefaultVideo = this.selTplVideo && this.selTpl.header_video_url && !this.tplImgChanged
        const hasDefaultDocument = this.selTplDocument && this.selTpl.header_document_url && !this.tplImgChanged

        if (hasFileUpload || hasDefaultImage || hasDefaultVideo || hasDefaultDocument) {
          const fd = new FormData()
          fd.append('type', 'template')
          fd.append('template_name', this.selTpl.name)
          fd.append('template_lang', this.selTpl.language || 'pt_BR')
          if (hasFileUpload) {
            if (this.selTplImg) fd.append('header_image', this.tplHeaderFile)
            else if (this.selTplVideo) fd.append('header_video', this.tplHeaderFile)
            else if (this.selTplDocument) fd.append('header_document', this.tplHeaderFile)
          } else if (hasDefaultImage) {
            fd.append('header_image_url', this.selTpl.header_image_url)
          } else if (hasDefaultVideo) {
            fd.append('header_video_url', this.selTpl.header_video_url)
          } else if (hasDefaultDocument) {
            fd.append('header_document_url', this.selTpl.header_document_url)
          }
          if (comps.length) fd.append('components', JSON.stringify(comps))
          await services.sendMessageFormData(this.selConvId, fd)
        } else {
          await this.sendMessage({
            id: this.selConvId,
            payload: {
              type: 'template',
              template_name: this.selTpl.name,
              template_lang: this.selTpl.language || 'pt_BR',
              components: comps
            }
          })
        }

        this.showTplModal = false
        this.showTplPreview = false
        this.selTpl = null
        successMessage('Template enviado')
        await this.loadMessages({ id: this.selConvId, params: { limit: 50 } })
        this.scroll()
        await this.refreshConvInSidebar()
        this.fetchConvs()
      } catch (e) {
        errorMessage(e.response?.data?.error || 'Erro')
      } finally {
        this.sending = false
      }
    },

    // Actions
    async doComplete () {
      if (!this.selConvId) return
      const id = this.selConvId
      try {
        await services.completeConversation(id)
        markAsRemoved(id)
        this.selConvId = null
        this.showDetail = false
        this.$store.commit('messenger/MESSENGER/SET_CONVERSATION', null)
        this.$store.commit('messenger/MESSENGER/SET_MESSAGES', [])
        this.$store.commit('messenger/MESSENGER/REMOVE_CONVERSATION', id)
        successMessage('Conversa concluída')
        // Recarregar lista da API após 1s (espera broadcast passar)
        setTimeout(() => { this.fetchConvs() }, 1000)
      } catch (e) {
        errorMessage('Erro ao concluir')
      }
    },
    async doReopen () {
      if (!this.selConvId) return
      try {
        await services.reopenConversation(this.selConvId)
        successMessage('Conversa reaberta')
        this.showingArchived = false
        this.activeFilter = 'all'
        await this.fetchConvs()
        await new Promise(function (resolve) { setTimeout(resolve, 500) })
        await this.selectConversation(this.selConvId)
      } catch (e) { errorMessage('Erro') }
    },
    async doUnarchive () {
      if (!this.selConvId) return
      try {
        await services.unarchiveConversation(this.selConvId)
        successMessage('Conversa desarquivada')
        this.showingArchived = false
        this.activeFilter = 'all'
        await this.fetchConvs()
        await this.selectConversation(this.selConvId)
      } catch (e) { errorMessage('Erro') }
    },
    goParticipant () { if (this.conv?.contact) this.$router.push(`/participants/${this.conv.contact.id}`) },
    openWA () { if (this.conv) window.open(`https://wa.me/55${this.conv.phone.replace(/\D/g, '')}`, '_blank') },

    // Transfer
    async loadOps () { this.loadingOps = true; try { const r = await services.getOperators(); this.operators = r.data.data || r.data || [] } catch (e) { this.operators = [] } finally { this.loadingOps = false } },
    async doAssign () { if (!this.selOp || !this.selConvId) return; try { await services.assignConversation(this.selConvId, this.selOp); this.showTransfer = false; successMessage('Transferido'); this.fetchConvs(); await this.selectConversation(this.selConvId) } catch (e) { errorMessage('Erro') } },
    async refreshConvInSidebar () {
      try {
        const res = await services.showConversation(this.selConvId)
        const updated = res.data
        // Forçar unread_count = 0 para conversa que está aberta
        if (this.selConvId && updated.id === this.selConvId) {
          updated.unread_count = 0
        }
        this.$store.commit('messenger/MESSENGER/UPDATE_CONVERSATION', updated)
      } catch (e) { /* silent */ }
    },
    async doAssumeSelf () {
      if (!this.selConvId) return
      try {
        const user = this.$store.state.auth?.user
        if (!user || !user.id) { errorMessage('Não foi possível identificar o operador'); return }
        await services.assignConversation(this.selConvId, user.id)
        // Se estava em arquivados, volta pra "Todos"
        if (this.showingArchived) {
          this.showingArchived = false
          this.activeFilter = 'all'
        }
        await this.fetchConvs()
        // Delay para garantir que a system message foi persistida antes de recarregar
        await new Promise(function (resolve) { setTimeout(resolve, 500) })
        await this.selectConversation(this.selConvId)
        successMessage('Conversa assumida por ' + user.name)
      } catch (e) {
        console.error('doAssumeSelf error:', e)
        errorMessage('Erro ao assumir conversa')
      }
    },

    // New conv
    searchParts () { clearTimeout(this.searchTO); this.searchTO = setTimeout(async () => { if (this.pSearch.length < 2) { this.pResults = []; return }; try { const r = await services.searchParticipants({ search: this.pSearch, per_page: 10 }); this.pResults = r.data.data || [] } catch (e) { this.pResults = [] } }, 400) },
    pickPart (p) { this.nc.participant_id = p.id; this.nc.name = p.name; this.nc.phone = p.phone.replace(/\D/g, ''); this.pResults = []; this.pSearch = '' },
    onNcTplChange () {
      const t = this.templates.find(x => x.name === this.nc.template_name); this.ncNeedsImg = false; this.ncImgFile = null; this.ncParams = []
      if (!t || !t.components) return; const h = t.components.find(c => c.type === 'HEADER'); if (h && h.format === 'IMAGE') this.ncNeedsImg = true
      const b = t.components.find(c => c.type === 'BODY'); if (b && b.text) { const ms = b.text.match(/\{\{(\d+)\}\}/g); this.ncParams = (ms || []).map(() => ({ value: '' })) }
      this.nc.template_lang = t.language || 'pt_BR'
    },
    async doStartConv () {
      this.sending = true
      try {
        const payload = {}
        if (this.nc.participant_id) {
          payload.participant_id = this.nc.participant_id
        } else {
          payload.phone = this.nc.phone
          payload.name = this.nc.name
        }
        const res = await services.startConversation(payload)
        const conv = res.data.conversation || res.data
        this.showNewConv = false
        this.nc = { name: '', phone: '', participant_id: null, template_name: '', template_lang: 'pt_BR' }
        this.pSearch = ''
        this.pResults = []
        this.showingArchived = false
        this.activeFilter = 'all'
        await this.fetchConvs()
        // Abre a conversa criada
        if (conv && conv.id) {
          await this.openConv(conv)
        }
        successMessage('Conversa criada')
      } catch (e) { errorMessage(e.response?.data?.error || e.response?.data?.message || 'Erro') } finally { this.sending = false }
    },

    // Pusher
    playNotificationSound () {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)()
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.frequency.value = 880
        osc.type = 'sine'
        gain.gain.setValueAtTime(0.3, ctx.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3)
        osc.start(ctx.currentTime)
        osc.stop(ctx.currentTime + 0.3)
        // Second tone
        const osc2 = ctx.createOscillator()
        const gain2 = ctx.createGain()
        osc2.connect(gain2)
        gain2.connect(ctx.destination)
        osc2.frequency.value = 1320
        osc2.type = 'sine'
        gain2.gain.setValueAtTime(0.2, ctx.currentTime + 0.15)
        gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4)
        osc2.start(ctx.currentTime + 0.15)
        osc2.stop(ctx.currentTime + 0.4)
      } catch (e) { /* silent fail */ }
    },
    setupPusher () {
      const slug = process.env.VUE_APP_EVENT_NAME || ''
      const channel = slug ? 'wa-messenger-' + slug : 'wa-messenger'
      this.pusherChannel = channel
      this.pusherCh = this.$pusher.subscribe(channel)
      this.pusherCh.bind('new-message', (d) => {
        this.receiveNewMessage(d)
        this.scroll()
        // Play sound if tab is not focused
        if (document.hidden) {
          this.playNotificationSound()
        }
      })
      this.pusherCh.bind('message-status-updated', (d) => { this.receiveStatusUpdate(d) })
    },
    tearPusher () { if (this.pusherCh) { this.pusherCh.unbind_all(); this.$pusher.unsubscribe(this.pusherChannel || 'wa-messenger') } }
  },
  watch: {
    messages () { this.scroll() },
    showTransfer (v) { if (v) this.loadOps() }
  },
  async mounted () {
    // Limpar notificacoes pendentes de WA (usuario entrou no messenger)
    localStorage.removeItem('wa_pending_notifications')

    await this.fetchConvs()
    await this.loadTemplates({ status: 'approved' })
    this.setupPusher()
    this.tickInterval = setInterval(() => { this.nowTs = moment() }, 1000)
  },
  beforeDestroy () {
    this.tearPusher()
    if (this.tickInterval) clearInterval(this.tickInterval)
  }
}
</script>

<style lang="scss">
@import './messenger.scss';
</style>
